import { TranslateService } from '@ngx-translate/core';
import { ERROR_CODE } from 'src/app/shared/models/error.models';
import { IOcrErrorResponse } from '../models/ocr';

/**
 * Extrae el código de país (ISO-2/3) del error OCR 2071.
 * Prioridad: campo `error.country` → placeholder `{X}` en msg → token ISO-3 en msg.
 */
export function extractOcrRejectedCountryCode(errorBody?: IOcrErrorResponse | null): string | null {
	const fromField = errorBody?.error?.country?.trim();
	if (fromField) {
		return fromField.toUpperCase();
	}

	const msg = errorBody?.error?.msg ?? '';
	const braced = msg.match(/\{([A-Za-z]{2,3})\}/);
	if (braced?.[1]) {
		return braced[1].toUpperCase();
	}

	const iso3 = msg.match(/\b([A-Z]{3})\b/);
	if (iso3?.[1]) {
		return iso3[1];
	}

	return null;
}

/** Etiqueta localizada del país (`COUNTRIES.XXX`) o el código si no hay traducción. */
export function resolveOcrCountryLabel(trans: TranslateService, countryCode: string | null): string {
	if (!countryCode) {
		return '';
	}
	const key = `COUNTRIES.${countryCode.toUpperCase()}`;
	const translated = trans.instant(key);
	return translated !== key ? translated : countryCode;
}

/**
 * Códigos OCR de restricción de documento/país (pantalla «Documento no aceptado»).
 * Incluye `2003` (DOC_TYPE_NOT_VALID) con el mismo copy que `2070`.
 */
export function isOcrRestrictionErrorCode(errorCode: string | null | undefined): boolean {
	return (
		errorCode === ERROR_CODE.DOC_TYPE_NOT_ALLOWED ||
		errorCode === ERROR_CODE.DOC_TYPE_NOT_VALID ||
		errorCode === ERROR_CODE.COUNTRY_NOT_ALLOWED
	);
}

/**
 * Mensaje i18n a mostrar en pantalla para restricciones OCR 2003/2070/2071.
 */
export function resolveOcrRestrictionErrorMessage(
	trans: TranslateService,
	errorCode: string,
	errorBody?: IOcrErrorResponse | null,
): string | null {
	if (errorCode === ERROR_CODE.DOC_TYPE_NOT_ALLOWED || errorCode === ERROR_CODE.DOC_TYPE_NOT_VALID) {
		return trans.instant('OCR.GENERAL.ERROR.DOC_TYPE_NOT_ALLOWED');
	}
	if (errorCode === ERROR_CODE.COUNTRY_NOT_ALLOWED) {
		const country = resolveOcrCountryLabel(trans, extractOcrRejectedCountryCode(errorBody));
		return trans.instant('OCR.GENERAL.ERROR.COUNTRY_NOT_ALLOWED', { country: country || '—' });
	}
	return null;
}

/**
 * Clave de audio precargado para restricciones OCR 2003/2070/2071.
 * El mensaje en pantalla puede interpolar país; el MP3 es la clave i18n estática.
 */
export function resolveOcrRestrictionErrorAudioKey(errorCode: string): string | null {
	if (errorCode === ERROR_CODE.DOC_TYPE_NOT_ALLOWED || errorCode === ERROR_CODE.DOC_TYPE_NOT_VALID) {
		return 'OCR.GENERAL.ERROR.DOC_TYPE_NOT_ALLOWED';
	}
	if (errorCode === ERROR_CODE.COUNTRY_NOT_ALLOWED) {
		return 'OCR.GENERAL.ERROR.COUNTRY_NOT_ALLOWED';
	}
	return null;
}
